import type { ColorRamp } from 'styles/functions/colorRampGenerator/colorRamp';

const grey: ColorRamp = {
  '05': '#fafafa',
  '10': '#eeeeee',
  '20': '#e0e0e0',
  '30': '#d2d1d1',
  '40': '#c2c1c2',
  '50': '#858589',
  '60': '#6d6d71',
  '70': '#555559',
  '80': '#3f3f42',
  '90': '#2a2a2b',
  '100': '#161617',
};

const purpleGrey: ColorRamp = {
  '05': '#f7f7fc',
  '10': '#e9e9f6',
  '20': '#d9d9ee',
  '30': '#c8c9e3',
  '40': '#b5b7d6',
  '50': '#737791',
  '60': '#616480',
  '70': '#50536c',
  '80': '#404259',
  '90': '#313146',
  '100': '#222233',
};

const red: ColorRamp = {
  '05': '#fef5f6',
  '10': '#fde7e9',
  '20': '#fcd7db',
  '30': '#fbc5ca',
  '40': '#fbb1b7',
  '50': '#FD5254',
  '60': '#ea272c',
  '70': '#bc2a2e',
  '80': '#91272a',
  '90': '#6a2123',
  '100': '#471819',
};

const green: ColorRamp = {
  '05': '#f2fcf9',
  '10': '#daf8ef',
  '20': '#b9f6e3',
  '30': '#8ff2d4',
  '40': '#7ae9c7',
  '50': '#09C590',
  '60': '#05a179',
  '70': '#027f61',
  '80': '#015e49',
  '90': '#003f32',
  '100': '#00231b',
};

const yellow: ColorRamp = {
  '05': '#fff8ed',
  '10': '#fff0da',
  '20': '#fee8c7',
  '30': '#fddfb1',
  '40': '#fcd49a',
  '50': '#EEAD51',
  '60': '#c1904a',
  '70': '#977440',
  '80': '#715933',
  '90': '#4e3e25',
  '100': '#2e2516',
};

const blue: ColorRamp = {
  '05': '#f2f6fd',
  '10': '#e1eafa',
  '20': '#cddcf8',
  '30': '#b9cef5',
  '40': '#a2bdf2',
  '50': '#467EE5',
  '60': '#3c68bb',
  '70': '#315393',
  '80': '#263f6f',
  '90': '#1a2b4d',
  '100': '#0e192f',
};

const brand: ColorRamp = {
  '05': '#f1f0ff',
  '10': '#e4e2ff',
  '20': '#d6d4ff',
  '30': '#c7c3ff',
  '40': '#b6b2ff',
  '50': '#716BFF',
  '60': '#5a53eb',
  '70': '#463fce',
  '80': '#37339a',
  '90': '#292767',
  '100': '#1b1a3d',
};

const newBrand: ColorRamp = {
  '05': '#fcfef1',
  '10': '#f8fdde',
  '20': '#f4fcc4',
  '30': '#effc9f',
  '40': '#eafa82',
  '50': '#D9EE50',
  '60': '#b0c13d',
  '70': '#89972c',
  '80': '#646e1d',
  '90': '#41480f',
  '100': '#212504',
};

const newGrey: ColorRamp = {
  '05': '#f5f5f5',
  '10': '#e9e9e9',
  '20': '#dcdcdc',
  '30': '#cecece',
  '40': '#bfbebf',
  '50': '#858589',
  '60': '#6d6d71',
  '70': '#555559',
  '80': '#3f3f42',
  '90': '#2a2a2b',
  '100': '#161617',
};

const purple: ColorRamp = {
  '05': '#f9f5fe',
  '10': '#f1e7fd',
  '20': '#e7d8fc',
  '30': '#dcc7fb',
  '40': '#d0b5fb',
  '50': '#9B6DFF',
  '60': '#884ff3',
  '70': '#7532dd',
  '80': '#5a2ea6',
  '90': '#412576',
  '100': '#2b1a4d',
};

const orange: ColorRamp = {
  '05': '#fff8f2',
  '10': '#ffefe2',
  '20': '#ffe5d1',
  '30': '#ffdabe',
  '40': '#ffcea9',
  '50': '#FF9B50',
  '60': '#e18024',
  '70': '#b86b2a',
  '80': '#925629',
  '90': '#6e4223',
  '100': '#4d2e1a',
};

const pink: ColorRamp = {
  '05': '#fdf0f6',
  '10': '#fbdfec',
  '20': '#f9cce0',
  '30': '#f7b6d2',
  '40': '#f69dc3',
  '50': '#E12C7D',
  '60': '#b92066',
  '70': '#931550',
  '80': '#6e0b3b',
  '90': '#4b0426',
  '100': '#280112',
};

/**
 * Zinc: Dark mode neutral scale
 *
 * NOTE: These colors are NOT from design. They are placeholder values
 * until design provides the official dark mode color palette.
 *
 * Manually defined to preserve exact color values used in the dark theme.
 *
 * Dark end (backgrounds):
 *   100 → Page background
 *   90  → Card/elevated surfaces
 *   80  → Hover states, secondary surfaces
 *   70  → Accent surfaces, borders
 *
 * Light end (text/icons):
 *   05  → Button text
 *   10  → Primary text/icons
 *   30  → Secondary text/icons
 *   50  → Helper text, disabled icons
 *   60  → Placeholder, disabled text
 */
const zinc: ColorRamp = {
  '05': '#F5F5F5', // Button text, lightest
  '10': '#EDEDED', // Primary text/icons
  '20': '#D4D4D8',
  '30': '#A1A1AA', // Secondary text/icons
  '40': '#8E8E93',
  '50': '#71717A', // Helper text, disabled icons
  '60': '#52525B', // Placeholder, disabled text
  '70': '#27272A', // Surface grey, borders
  '80': '#18181B', // Dark grey, hover states
  '90': '#121217', // Card backgrounds
  '100': '#050509', // Page background
};

const baseColors = {
  white: '#FFFFFF',
  offWhite: '#FCFCFC',
  black: '#000000',
  grey,
  purpleGrey,
  red,
  green,
  yellow,
  blue,
  brand,
  newBrand,
  newGrey,
  purple,
  orange,
  pink,
  zinc, // dark mode neutral scale
};

export default baseColors;
